import { getErrorMessage } from "../config/errorConfig";
import { placeholderImage, toPublicStorageUrl } from "../config/imageConfig";
import {
  PRODUCT_SUB_CAT_URL,
  PRODUCT_ACCESSORIES_URL,
  PRODUCT_ONSALE_URL,
  PRODUCT_CATEGORY_URL,
  TTS_URL,
  NEW_ARRIVALS_URL,
} from "../config/apiConfig";

// The browse endpoints below are the Orbiee Commerce API's since 2026-10-20
// (they were the Cloud Functions' `*_dev` twins). The listing endpoint
// followed on 2026-10-28: POST /products_sub_cat takes the body
// products_v1_dev took and answers a plain array of the same rows, so only
// the host changed here (the old function answers 500 today).
const API_URL = PRODUCT_SUB_CAT_URL;
const WOMEN_API_URL = PRODUCT_CATEGORY_URL;

// Cache for /product_category (shared by the Women and Men pages and, via
// categoriesApi, the navbar)
let productCategoryDevCache = { data: null, timestamp: null };
const PRODUCT_CATEGORY_DEV_TTL = 10 * 60 * 1000; // 10 minutes

// Cache for /product_accessories (shared by Accessories page and Navbar)
let accessoriesDevCache = { data: null, timestamp: null };
const ACCESSORIES_DEV_TTL = 10 * 60 * 1000; // 10 minutes

// Cache for /product_onsale (shared by Sale page and Navbar)
let onSaleDevCache = { data: null, timestamp: null };
const ONSALE_DEV_TTL = 10 * 60 * 1000; // 10 minutes

/**
 * Fetch products by category/subcategory (Commerce API POST /products_sub_cat).
 * @param {string} category - Category name (e.g. "Dresses", "Shirts", "Accessories")
 * @param {string} subcategory - Subcategory / product type (e.g. "Casual Dresses", "Casual Shirts")
 * @param {boolean} [onSale=false] - If true, sends on_sale: "yes"
 * @param {string} [gender="women"] - "women" or "men" (a required field; 422 without it)
 * @param {Object} [options] - Optional filters: colour_group_name, perceived_colour_value_name, perceived_colour_master_name, graphical_appearance_name
 */
export const fetchProductsByCategory = async (
  category,
  subcategory,
  onSale = false,
  gender = "women",
  options = {}
) => {
  if (!category || !subcategory) {
    throw new Error(getErrorMessage("VALIDATION_CATEGORY_REQUIRED"));
  }

  try {
    const requestBody = {
      gender: gender === "men" ? "men" : "women",
      category: category.trim(),
      subcategory: subcategory.trim(),
    };

    if (onSale) {
      requestBody.on_sale = "yes";
    }
    if (options.colour_group_name) requestBody.colour_group_name = options.colour_group_name;
    if (options.perceived_colour_value_name) requestBody.perceived_colour_value_name = options.perceived_colour_value_name;
    if (options.perceived_colour_master_name) requestBody.perceived_colour_master_name = options.perceived_colour_master_name;
    if (options.graphical_appearance_name) requestBody.graphical_appearance_name = options.graphical_appearance_name;

    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    // Handle 404 or other HTTP errors
   if (!response.ok) {
         // Map HTTP status to user-friendly error
         let errorMessage;
         if (response.status >= 500) {
           errorMessage = getErrorMessage("API_SERVER_ERROR");
         } else if (response.status === 404) {
           errorMessage = getErrorMessage("API_NOT_FOUND");
         } else if (response.status === 429) {
           errorMessage = getErrorMessage("API_RATE_LIMITED");
         } else {
           errorMessage = getErrorMessage("API_BAD_REQUEST");
         }
   
         throw new Error(errorMessage);
       }

    let data;
    try {
      data = await response.json();
    } catch (err) {
      throw new Error(getErrorMessage("API_INVALID_JSON"));
    }

    // Safely handle response whether it's array or nested
    const productArray = Array.isArray(data)
      ? data
      : Array.isArray(data.products)
      ? data.products
      : [];

    // Map to standard format
   return productArray.map(p => {
      const price = parseFloat(p.price) || 0;
      const discountedPrice = parseFloat(p.discounted_price) || price;
      const isOnPromotion = discountedPrice < price;
      
      return {
        id: p.article_id,
        name: p.enhanced_prod_name || p.prod_name || "Unnamed Product",
        price: price,
        discounted_price: discountedPrice,
        isOnPromotion: isOnPromotion,
        image: toPublicStorageUrl((p.image_url || p.image_uri || '').trim())
          || placeholderImage(p.article_id, 400, 600),
        detail_desc: p.detail_desc || 'No description available.'
      };
    });
  } catch (error) {
    if (error.message.includes("Failed to fetch")) {
      throw new Error(getErrorMessage("NETWORK_SERVER_UNREACHABLE"));
    }else{
        throw error;
    }
  }
};

/**
 * Normalize product_types array from accessories API
 * @param {Array} arr - product_types from API
 * @returns {Array} Normalized list with product_type_name, product_count, image_url, index
 */
function normalizeAccessoryTypes(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof item.product_type_name === "string" &&
        item.product_type_name.trim() !== ""
    )
    .map((item) => ({
      product_type_name: item.product_type_name.trim(),
      product_count: typeof item.product_count === "number" ? item.product_count : 0,
      image_url: item.image_url || null,
      index: typeof item.index === "number" ? item.index : null,
    }));
}

/**
 * Fetch women and men accessories from /product_accessories API.
 * Cached so one call serves Accessories page and Navbar.
 * @returns {Promise<{ women: { total_count: number, product_types: Array }, men: { total_count: number, product_types: Array } }>}
 */
export const fetchAccessoriesDev = async () => {
  const now = Date.now();
  if (
    accessoriesDevCache.data &&
    accessoriesDevCache.timestamp &&
    now - accessoriesDevCache.timestamp < ACCESSORIES_DEV_TTL
  ) {
    return accessoriesDevCache.data;
  }

  try {
    const response = await fetch(PRODUCT_ACCESSORIES_URL, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      let errorMessage;
      if (response.status >= 500) {
        errorMessage = getErrorMessage("API_SERVER_ERROR");
      } else if (response.status === 404) {
        errorMessage = getErrorMessage("API_NOT_FOUND");
      } else if (response.status === 429) {
        errorMessage = getErrorMessage("API_RATE_LIMITED");
      } else {
        errorMessage = getErrorMessage("API_BAD_REQUEST");
      }
      throw new Error(errorMessage);
    }

    let data;
    try {
      data = await response.json();
    } catch (err) {
      throw new Error(getErrorMessage("API_INVALID_JSON"));
    }

    const women = {
      total_count: typeof data.women?.total_count === "number" ? data.women.total_count : 0,
      product_types: normalizeAccessoryTypes(data.women?.product_types || []),
    };
    const men = {
      total_count: typeof data.men?.total_count === "number" ? data.men.total_count : 0,
      product_types: normalizeAccessoryTypes(data.men?.product_types || []),
    };

    accessoriesDevCache = {
      data: { women, men },
      timestamp: Date.now(),
    };

    return accessoriesDevCache.data;
  } catch (error) {
    if (error.message.includes("Failed to fetch")) {
      throw new Error(getErrorMessage("NETWORK_SERVER_UNREACHABLE"));
    }
    throw error;
  }
};

/**
 * Fetch list of accessory product types (uses /product_accessories; returns merged women + men for Navbar dropdown)
 * @returns {Promise<Array<{product_type_name: string, product_count: number, image_url: string|null, index: number|null}>>}
 */
export const fetchAccessoriesTypes = async () => {
  try {
    const { women, men } = await fetchAccessoriesDev();
    const all = [
      ...(women.product_types || []),
      ...(men.product_types || []),
    ];
    return all;
  } catch (error) {
    if (error.message.includes("Failed to fetch")) {
      throw new Error(getErrorMessage("NETWORK_SERVER_UNREACHABLE"));
    }
    throw error;
  }
};

/**
 * Normalize product_types array from on sale API (includes product_group_name)
 * @param {Array} arr - product_types from API
 * @returns {Array} Normalized list with product_type_name, product_group_name, product_count, image_url, index
 */
function normalizeOnSaleTypes(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof item.product_type_name === "string" &&
        item.product_type_name.trim() !== ""
    )
    .map((item) => ({
      product_type_name: item.product_type_name.trim(),
      product_group_name:
        item?.product_group_name && typeof item.product_group_name === "string"
          ? item.product_group_name.trim()
          : undefined,
      product_count: typeof item.product_count === "number" ? item.product_count : 0,
      image_url: item.image_url || null,
      index: typeof item.index === "number" ? item.index : null,
    }));
}

/**
 * Fetch women and men on-sale types from /product_onsale API.
 * Cached so one call serves Sale page and Navbar.
 * @returns {Promise<{ women: { total_count: number, product_types: Array }, men: { total_count: number, product_types: Array } }>}
 */
export const fetchOnSaleDev = async () => {
  const now = Date.now();
  if (
    onSaleDevCache.data &&
    onSaleDevCache.timestamp &&
    now - onSaleDevCache.timestamp < ONSALE_DEV_TTL
  ) {
    return onSaleDevCache.data;
  }

  try {
    const response = await fetch(PRODUCT_ONSALE_URL, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      let errorMessage;
      if (response.status >= 500) {
        errorMessage = getErrorMessage("API_SERVER_ERROR");
      } else if (response.status === 404) {
        errorMessage = getErrorMessage("API_NOT_FOUND");
      } else if (response.status === 429) {
        errorMessage = getErrorMessage("API_RATE_LIMITED");
      } else {
        errorMessage = getErrorMessage("API_BAD_REQUEST");
      }
      throw new Error(errorMessage);
    }

    let data;
    try {
      data = await response.json();
    } catch (err) {
      throw new Error(getErrorMessage("API_INVALID_JSON"));
    }

    const women = {
      total_count: typeof data.women?.total_count === "number" ? data.women.total_count : 0,
      product_types: normalizeOnSaleTypes(data.women?.product_types || []),
    };
    const men = {
      total_count: typeof data.men?.total_count === "number" ? data.men.total_count : 0,
      product_types: normalizeOnSaleTypes(data.men?.product_types || []),
    };

    onSaleDevCache = {
      data: { women, men },
      timestamp: Date.now(),
    };

    return onSaleDevCache.data;
  } catch (error) {
    if (error.message.includes("Failed to fetch")) {
      throw new Error(getErrorMessage("NETWORK_SERVER_UNREACHABLE"));
    }
    throw error;
  }
};

/**
 * Fetch list of on sale product types (uses /product_onsale; returns merged women + men for Navbar dropdown)
 * @returns {Promise<Array<{product_type_name: string, product_group_name?: string, product_count: number, image_url: string|null, index: number|null}>>}
 */
export const fetchOnSaleTypes = async () => {
  try {
    const { women, men } = await fetchOnSaleDev();
    const all = [
      ...(women.product_types || []),
      ...(men.product_types || []),
    ];
    return all;
  } catch (error) {
    if (error.message.includes("Failed to fetch")) {
      throw new Error(getErrorMessage("NETWORK_SERVER_UNREACHABLE"));
    }
    throw error;
  }
};

/**
 * Fetch list of women product categories with subcategories
 * @returns {Promise<Array<{category: string, index: number, subcategories: Array, total_count: number}>>} Array of category objects with subcategories
 */
export const fetchWomenTypes = async () => {
  try {
    const response = await fetch(WOMEN_API_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      let errorMessage;
      if (response.status >= 500) {
        errorMessage = getErrorMessage("API_SERVER_ERROR");
      } else if (response.status === 404) {
        errorMessage = getErrorMessage("API_NOT_FOUND");
      } else if (response.status === 429) {
        errorMessage = getErrorMessage("API_RATE_LIMITED");
      } else {
        errorMessage = getErrorMessage("API_BAD_REQUEST");
      }
      throw new Error(errorMessage);
    }

    let data;
    try {
      data = await response.json();
    } catch (err) {
      throw new Error(getErrorMessage("API_INVALID_JSON"));
    }

    // Handle response structure: [{ category: "...", index: 1, subcategories: [...], total_count: ... }, ...]
    if (Array.isArray(data)) {
      return data
        .filter(item => 
          item && 
          typeof item === 'object' &&
          typeof item.category === 'string' &&
          Array.isArray(item.subcategories)
        )
        .map(item => ({
          category: item.category.trim(),
          index: typeof item.index === 'number' ? item.index : null,
          subcategories: item.subcategories
            .filter(sub => 
              sub && 
              typeof sub === 'object' &&
              typeof sub.product_type_name === 'string' &&
              sub.product_type_name.trim() !== ''
            )
            .map(sub => ({
              product_type_name: sub.product_type_name.trim(),
              product_count: typeof sub.product_count === 'number' ? sub.product_count : 0,
              image_url: sub.image_url || null
            })),
          total_count: typeof item.total_count === 'number' ? item.total_count : 0
        }));
    }

    return [];
  } catch (error) {
    if (error.message.includes("Failed to fetch")) {
      throw new Error(getErrorMessage("NETWORK_SERVER_UNREACHABLE"));
    } else {
      throw error;
    }
  }
};

/**
 * Normalize a category array from /product_category API shape
 * @param {Array} arr - Raw category list from API
 * @returns {Array} Normalized list with category, index, total_count, subcategories
 */
function normalizeCategoryList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof item.category === "string" &&
        Array.isArray(item.subcategories)
    )
    .map((item) => ({
      category: item.category.trim(),
      index: typeof item.index === "number" ? item.index : null,
      total_count: typeof item.total_count === "number" ? item.total_count : 0,
      subcategories: (item.subcategories || [])
        .filter(
          (sub) =>
            sub &&
            typeof sub === "object" &&
            typeof sub.product_type_name === "string" &&
            sub.product_type_name.trim() !== ""
        )
        .map((sub) => ({
          product_type_name: sub.product_type_name.trim(),
          product_count: typeof sub.product_count === "number" ? sub.product_count : 0,
          image_url: sub.image_url || null,
        })),
    }));
}

/**
 * Fetch women and men categories from the Commerce API's /product_category.
 * Cached so one call serves the Women and Men pages and the navbar
 * (categoriesApi.fetchCategories reads the `women` half).
 * @returns {Promise<{ women: Array, men: Array }>}
 */
export const fetchProductCategoriesDev = async () => {
  const now = Date.now();
  if (
    productCategoryDevCache.data &&
    productCategoryDevCache.timestamp &&
    now - productCategoryDevCache.timestamp < PRODUCT_CATEGORY_DEV_TTL
  ) {
    return productCategoryDevCache.data;
  }

  try {
    const response = await fetch(PRODUCT_CATEGORY_URL, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      let errorMessage;
      if (response.status >= 500) {
        errorMessage = getErrorMessage("API_SERVER_ERROR");
      } else if (response.status === 404) {
        errorMessage = getErrorMessage("API_NOT_FOUND");
      } else if (response.status === 429) {
        errorMessage = getErrorMessage("API_RATE_LIMITED");
      } else {
        errorMessage = getErrorMessage("API_BAD_REQUEST");
      }
      throw new Error(errorMessage);
    }

    let data;
    try {
      data = await response.json();
    } catch (err) {
      throw new Error(getErrorMessage("API_INVALID_JSON"));
    }

    const women = normalizeCategoryList(data.women || []);
    const men = normalizeCategoryList(data.men || []);

    productCategoryDevCache = {
      data: { women, men },
      timestamp: Date.now(),
    };

    return productCategoryDevCache.data;
  } catch (error) {
    if (error.message.includes("Failed to fetch")) {
      throw new Error(getErrorMessage("NETWORK_SERVER_UNREACHABLE"));
    }
    throw error;
  }
};

/**
 * Convert text to speech using TTS API
 * @param {string} text - Text to convert to speech
 * @returns {Promise<{status: string, audio_base64: string, mime_type: string, voice_used: string}|null>} TTS response or null on error
 */
export const convertTextToSpeech = async (text) => {
  if (!text || !text.trim()) {
    return null;
  }

  try {
    const response = await fetch(
      TTS_URL,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: text.trim() })
      }
    );

    if (!response.ok) {
      console.error('TTS API error:', response.status);
      return null;
    }

    const data = await response.json();
    
    if (data.status === 'success' && data.audio_base64) {
      return data;
    }

    return null;
  } catch (error) {
    console.error('TTS API error:', error);
    return null;
  }
};

/**
 * Fetch new arrivals with pagination.
 * GET /new_arrivals?page=<page>&page_size=<pageSize> (Commerce API)
 * Returns: { page, page_size, products, total_pages, total_records }
 */
export const fetchNewArrivals = async ({ page = 1, pageSize = 24 } = {}) => {
  const url = `${NEW_ARRIVALS_URL}?page=${page}&page_size=${pageSize}`;
  const response = await fetch(url, { method: "GET" });
  if (!response.ok) {
    const text = await response.text().catch(() => `HTTP ${response.status}`);
    throw new Error(`New arrivals API error: ${response.status} — ${text}`);
  }
  return response.json();
};